/**
 * Per-user persistence of guided-tutorial progress.
 *
 * State lives in localStorage (frontend-only), namespaced by username so
 * multiple accounts on the same device keep separate progress.
 *  - `completed`: tutorial ids the user finished OR skipped (drives the ✓ in
 *    the menu and prevents auto-replay). This is the only signal auto-start
 *    consults — an abandoned tour re-offers on the next screen visit.
 *  - `autoStarted`: legacy one-shot marker kept for data compatibility; no
 *    longer read (it used to permanently suppress re-offering).
 */

const STORAGE_KEY = 'cgm.tutorials.v1';

/**
 * Derive a stable per-user key from the cached `user_info` blob. Falls back to
 * 'anon' when no username is available (older sessions / logged out).
 */
export function getUserKey(): string {
  try {
    const raw = localStorage.getItem('user_info');
    if (!raw) return 'anon';
    const parsed = JSON.parse(raw);
    const username = parsed?.username;
    return typeof username === 'string' && username.length > 0 ? username : 'anon';
  } catch {
    return 'anon';
  }
}

export interface UserTutorialState {
  completed: string[];
  autoStarted: string[];
  /** The one-time "welcome to the tours" modal has been shown. */
  welcomeSeen: boolean;
}

type TutorialsMap = Record<string, UserTutorialState>;

function loadMap(): TutorialsMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as TutorialsMap) : {};
  } catch {
    return {};
  }
}

function saveMap(map: TutorialsMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* localStorage unavailable — fail silently, tutorial just re-offers next load */
  }
}

function sanitize(entry: unknown): UserTutorialState {
  const e = (entry ?? {}) as Partial<UserTutorialState>;
  return {
    completed: Array.isArray(e.completed) ? e.completed.filter((id) => typeof id === 'string') : [],
    autoStarted: Array.isArray(e.autoStarted) ? e.autoStarted.filter((id) => typeof id === 'string') : [],
    welcomeSeen: e.welcomeSeen === true,
  };
}

/** Load the given user's tutorial progress (defensive defaults on bad data). */
export function loadTutorialState(userKey: string): UserTutorialState {
  return sanitize(loadMap()[userKey]);
}

/** Mark a tutorial as completed (finished or skipped) and persist. */
export function markCompleted(userKey: string, id: string): void {
  const map = loadMap();
  const state = sanitize(map[userKey]);
  if (!state.completed.includes(id)) state.completed.push(id);
  map[userKey] = state;
  saveMap(map);
}

/**
 * Mark many tutorials completed + auto-started in one write. Used by "Skip all
 * tours" so dismissing one coachmark opts out of the entire auto-start chain
 * (and future first-visit nags) without needing N separate storage round-trips.
 */
export function markAllCompleted(userKey: string, ids: string[]): void {
  if (ids.length === 0) return;
  const map = loadMap();
  const state = sanitize(map[userKey]);
  for (const id of ids) {
    if (!state.completed.includes(id)) state.completed.push(id);
    if (!state.autoStarted.includes(id)) state.autoStarted.push(id);
  }
  map[userKey] = state;
  saveMap(map);
}

/** Record that the one-time welcome modal has been shown to this user. */
export function markWelcomeSeen(userKey: string): void {
  const map = loadMap();
  const state = sanitize(map[userKey]);
  state.welcomeSeen = true;
  map[userKey] = state;
  saveMap(map);
}

/** Remove a tutorial from the user's completed list (Replay affordance / QA). */
export function resetTutorial(userKey: string, id: string): void {
  const map = loadMap();
  const state = sanitize(map[userKey]);
  state.completed = state.completed.filter((c) => c !== id);
  map[userKey] = state;
  saveMap(map);
}
